window.KlaviyoCompositeCatalog = {
  INTEGRATION_TYPE: '$custom',
  CATALOG_TYPE: '$default',
  CATEGORY_EXTERNAL_ID: 'composite-catalog',

  slugify(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  },

  getOptionIndex(options, pattern) {
    if (!Array.isArray(options)) return -1;

    return options.findIndex((option) => pattern.test(String(option || '')));
  },

  getVariantOptionValue(variant, optionIndex) {
    if (optionIndex < 0 || !variant) return '';
    return variant[`option${optionIndex + 1}`] || '';
  },

  resolveTrueColour(variant, productConfig) {
    if (productConfig?.true_colour) return productConfig.true_colour;

    const colorIndex = this.getOptionIndex(productConfig?.options, /color|colour/i);
    const colourFromVariant = this.getVariantOptionValue(variant, colorIndex);

    if (colourFromVariant) return colourFromVariant;

    return variant?.option1 || '';
  },

  resolveSize(variant, productConfig) {
    const sizeIndex = this.getOptionIndex(productConfig?.options, /^size$/i);
    const sizeFromVariant = this.getVariantOptionValue(variant, sizeIndex);

    if (sizeFromVariant) return sizeFromVariant;

    const options = [variant?.option1, variant?.option2, variant?.option3].filter(Boolean);
    return options[options.length - 1] || '';
  },

  buildCompositeId({ title, trueColour, size }) {
    return [this.slugify(title), this.slugify(trueColour), this.slugify(size)].filter(Boolean).join('--');
  },

  buildItemExternalId({ title, trueColour }) {
    return [this.slugify(title), this.slugify(trueColour)].filter(Boolean).join('--');
  },

  /**
   * Shopify CDN often returns protocol-relative URLs (`//cdn...`).
   * Email clients need absolute https URLs for images to render.
   */
  toAbsoluteImageUrl(url) {
    const value = String(url || '').trim();
    if (!value) return '';
    if (value.startsWith('//')) return `https:${value}`;
    if (value.startsWith('http://')) return `https://${value.slice(7)}`;
    return value;
  },

  buildCatalogPayload({ productConfig, variant }) {
    const title = productConfig?.title || '';
    const trueColour = this.resolveTrueColour(variant, productConfig);
    const size = this.resolveSize(variant, productConfig);
    const compositeId = this.buildCompositeId({ title, trueColour, size });
    const itemExternalId = this.buildItemExternalId({ title, trueColour });
    const imageUrl = this.toAbsoluteImageUrl(
      variant?.featured_image?.src ||
        variant?.image?.src ||
        variant?.featured_media?.preview_image?.src ||
        productConfig?.featured_image ||
        ''
    );
    const price = typeof variant?.price === 'number' ? variant.price / 100 : Number(variant?.price || 0) / 100;

    return {
      composite_id: compositeId,
      item_external_id: itemExternalId,
      variant_external_id: compositeId,
      title,
      true_colour: trueColour,
      size,
      description: productConfig?.description || title,
      url: productConfig?.url || window.location.href,
      image_full_url: imageUrl,
      image_thumbnail_url: imageUrl,
      price,
      sku: variant?.sku || '',
      shopify_variant_id: variant?.id || null,
      shopify_product_id: productConfig?.id || null,
      inventory_quantity: Number(variant?.inventory_quantity || 0),
      variant_title: [trueColour, size].filter(Boolean).join(' / '),
    };
  },

  parseDataJetPayload(payload) {
    if (!payload || typeof payload !== 'object') return {};

    if (payload.body && typeof payload.body === 'object') {
      return payload.body;
    }

    return payload;
  },

  formatDataJetError(response, payload) {
    const body = this.parseDataJetPayload(payload);
    const message = body.message || body.error;

    if (body.klaviyo_debug) {
      console.error('[Back in stock] Klaviyo debug', body.klaviyo_debug);
      const debugBody = body.klaviyo_debug.body;
      const klaviyoDetail =
        debugBody?.errors?.[0]?.detail ||
        debugBody?.errors?.[0]?.title ||
        (typeof debugBody === 'string' ? debugBody : null);

      if (klaviyoDetail) {
        return message ? `${message} ${klaviyoDetail}` : klaviyoDetail;
      }
    }

    if (typeof message === 'string' && message.trim()) {
      return message;
    }

    if (typeof body.detail === 'string' && body.detail.trim()) {
      return body.detail;
    }

    if (body.detail && typeof body.detail === 'object') {
      const klaviyoDetail = body.detail?.errors?.[0]?.detail || body.detail?.errors?.[0]?.title;
      if (klaviyoDetail) return klaviyoDetail;
    }

    if (response.status === 429) {
      return 'Too many requests. Please try again in a minute.';
    }

    return `Unable to save back in stock subscription. (HTTP ${response.status})`;
  },

  async subscribeViaDataJet({ taskId, email, marketingConsent, catalog }) {
    if (!taskId) {
      throw new Error('Back in stock subscribe task is not configured.');
    }

    if (!catalog?.composite_id || !catalog?.title || !catalog?.url) {
      throw new Error('Missing product details for back in stock subscription.');
    }

    const response = await fetch(`/apps/datajet/task/${taskId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        email,
        marketing_consent: Boolean(marketingConsent),
        composite_id: catalog.composite_id,
        item_external_id: catalog.item_external_id,
        title: catalog.title,
        true_colour: catalog.true_colour,
        size: catalog.size,
        description: catalog.description,
        url: catalog.url,
        image_full_url: catalog.image_full_url,
        price: catalog.price,
        sku: catalog.sku,
        inventory_quantity: catalog.inventory_quantity,
        variant_title: catalog.variant_title,
      }),
    });

    const rawPayload = await response.json().catch(() => ({}));
    const payload = this.parseDataJetPayload(rawPayload);

    if (!response.ok || payload.status === 'error') {
      console.error('[Back in stock] DataJet subscribe failed', {
        status: response.status,
        payload: rawPayload,
      });
      throw new Error(this.formatDataJetError(response, rawPayload));
    }

    return payload;
  },
};
